import { gameKeys, games } from "./games";
import { buildTicketExplanation } from "./explanation";
import { buildDataHealth, dataHealthStatusForGame } from "./dataHealth";
import { buildPlayProjection } from "./projection";
import { buildSourceQuality, sourceGradeLabel } from "./sourceQuality";
import {
  antiPopularityTicket,
  compactTicket,
  coverageBonus,
  createTicket,
  diversityTicket,
  humanFeatureVector,
  isoDate,
  jackpotProbability,
  lowerTierEv,
  matchProbability,
  mulberry32,
  nextDrawDate,
  parseIsoDate,
  poissonSplitShareFactor,
  prizeChanceSummary,
  publicPickProbability,
  randomTicket,
  safeExp,
  stableSeed,
  ticketCost,
  ticketFeatures,
  ticketOverlap,
  toPayload,
  totalCombinations,
  validateTicket,
  weekId,
  weekdayMon0
} from "./math";
import type {
  AdvisorPayload,
  AiAuditFactor,
  AiSystemAudit,
  AiSystemGrade,
  DataHealthSummary,
  FullRecommendation,
  GameDataStatus,
  GameConfig,
  GameKey,
  GameOpportunity,
  GameRecommendation,
  JackpotSnapshot,
  LearningScopeSummary,
  ModelContributionLine,
  ModelKey,
  ModelVersionReceipt,
  OutsideSignalAuditSummary,
  PersonalCalibrationSummary,
  RecommendationConfig,
  RuntimeData,
  SourceFreshnessSummary,
  SourceQualityGrade,
  Ticket,
  TicketScore
} from "./types";

export const ENGINE_VERSION = "lotto-engine-v22";
export const RECOMMENDATION_SCHEMA_VERSION = "advisor-receipt-v20";

export const defaultConfig: RecommendationConfig = {
  advisor_mode: "single_player",
  selection_goal: "jackpot_first",
  budget: 50,
  user_id: "me",
  tax_keep_rate: 0.58,
  candidate_count: 100_000,
  keep_top: 5_000,
  rotation_mode: "draw",
  draw_bias_mode: "auto",
  use_powerplay: false,
  risk_aversion: 0,
  max_overlap: 2,
  quick_pick_share: 0.7,
  random_seed_extra: ""
};

interface CandidatePoolStats {
  legalTicketSpace: number;
  ticketsTested: number;
  searchAttempts: number;
  medianScore: number;
  medianAfterTaxValue: number;
}

interface CandidatePoolResult {
  tickets: TicketScore[];
  stats: CandidatePoolStats;
}

export const DEFAULT_SALES: Record<GameKey, number> = {
  powerball: 20_000_000,
  mega_millions: 25_000_000,
  millionaire_for_life: 3_000_000
};

export function emptyRuntimeData(): RuntimeData {
  return {
    jackpots: {},
    jackpotSnapshots: [],
    modelStates: {},
    registry: [],
    coverage: undefined,
    externalCoverage: undefined
  };
}

export function normalizeConfig(input: Partial<RecommendationConfig> = {}): RecommendationConfig {
  return {
    ...defaultConfig,
    ...input,
    advisor_mode: input.advisor_mode === "portfolio" ? "portfolio" : "single_player",
    selection_goal: input.selection_goal === "value_first" ? "value_first" : "jackpot_first",
    budget: Number(input.budget ?? defaultConfig.budget),
    tax_keep_rate: Number(input.tax_keep_rate ?? defaultConfig.tax_keep_rate),
    candidate_count: Math.max(100, Math.trunc(Number(input.candidate_count ?? defaultConfig.candidate_count))),
    keep_top: Math.max(10, Math.trunc(Number(input.keep_top ?? defaultConfig.keep_top))),
    quick_pick_share: Number(input.quick_pick_share ?? defaultConfig.quick_pick_share)
  };
}

function latestSnapshot(data: RuntimeData, gameKey: GameKey): JackpotSnapshot | undefined {
  if (data.jackpots[gameKey]) return data.jackpots[gameKey];
  return data.jackpotSnapshots
    .filter((row) => row.game_key === gameKey)
    .sort((a, b) => `${b.draw_date}:${b.observed_at}`.localeCompare(`${a.draw_date}:${a.observed_at}`))[0];
}

export function jackpotForRecommendation(data: RuntimeData, gameKey: GameKey, now = new Date()): [string, number | null, number, string] {
  const game = games[gameKey];
  const scheduledDrawDate = nextDrawDate(now, game.drawDays);
  const row = latestSnapshot(data, gameKey);
  if (row) {
    if (row.draw_date >= scheduledDrawDate) return [row.draw_date, row.jackpot_annuity, row.jackpot_cash, row.source];
    if (gameKey === "millionaire_for_life" || row.source === "static_official_prize_chart") return [scheduledDrawDate, row.jackpot_annuity, row.jackpot_cash, row.source];
    return [scheduledDrawDate, row.jackpot_annuity, row.jackpot_cash, `stale_snapshot_reused:${row.source}`];
  }
  return [scheduledDrawDate, null, game.jackpotCashDefault, "default_no_live_snapshot"];
}

export function exactOrLatestCashForDraw(
  data: RuntimeData,
  gameKey: GameKey,
  drawDate: string,
  now = new Date()
): { cash: number; annuity: number | null; source: string } {
  const exact = data.jackpotSnapshots
    .filter((row) => row.game_key === gameKey && row.draw_date === drawDate)
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))[0];
  if (exact) return { cash: exact.jackpot_cash, annuity: exact.jackpot_annuity, source: `exact_snapshot:${exact.source}` };
  const [_, annuity, cash, source] = jackpotForRecommendation(data, gameKey, now);
  if (gameKey === "millionaire_for_life") return { cash, annuity, source: "fixed_top_cash_prize" };
  return { cash, annuity, source: `latest_snapshot_reused_not_future_forecast:${source}` };
}

function modelState(data: RuntimeData, gameKey: GameKey, modelKey: ModelKey): Record<string, unknown> {
  return data.modelStates[gameKey]?.[modelKey] ?? {};
}

function jackpotSourceLabel(source: string, gameKey: GameKey): { label: string; kind: GameDataStatus["jackpot_source_kind"] } {
  if (source.startsWith("stale_snapshot_reused:")) return { label: "Last live prize", kind: "live" };
  if (source === "texas_lottery_current") return { label: "Live prize", kind: "live" };
  if (source === "static_official_prize_chart" || gameKey === "millionaire_for_life") return { label: "Fixed prize", kind: "fixed" };
  if (source === "client_default" || source === "default_no_live_snapshot") return { label: "No live prize yet", kind: "fallback" };
  return { label: "Live source", kind: "live" };
}

function daysSinceDate(date: string | null | undefined, now: Date): number | null {
  if (!date) return null;
  const normalized = date.includes("T") ? date : date.includes(" ") ? `${date.replace(" ", "T")}Z` : `${date}T12:00:00Z`;
  const d = new Date(normalized);
  const ms = now.getTime() - d.getTime();
  if (!Number.isFinite(ms)) return null;
  return ms / 86400000;
}

function freshnessStatus(days: number | null, currentAfterDays: number, staleAfterDays: number): "current" | "stale" | "missing" {
  if (days === null) return "missing";
  if (days <= currentAfterDays) return "current";
  if (days <= staleAfterDays) return "stale";
  return "missing";
}

function latestEvent(data: RuntimeData, key: string) {
  return [...(data.sourceEvents ?? [])]
    .filter((event) => event.source_key === key)
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))[0];
}

function activeComponents(data: RuntimeData): ModelVersionReceipt["active_components"] {
  const active = { draw_bias: false, sales_forecast: false, human_pick: false, external_factor_audit: false };
  for (const gameKey of gameKeys) {
    for (const key of Object.keys(active) as Array<keyof typeof active>) {
      active[key] = active[key] || Boolean(data.modelStates[gameKey]?.[key]?.active);
    }
  }
  return active;
}

function rulesVersion(): string {
  return `rules_${stableSeed(gameKeys.map((key) => `${key}:${games[key].currentRuleStart}:${games[key].whiteMax}:${games[key].bonusMax}`).join("|"))
    .toString(16)
    .padStart(8, "0")}`;
}

function buildModelVersion(data: RuntimeData, sourceFreshness: SourceFreshnessSummary, generatedAt: string): ModelVersionReceipt {
  return {
    engine_version: ENGINE_VERSION,
    rules_version: rulesVersion(),
    recommendation_schema_version: RECOMMENDATION_SCHEMA_VERSION,
    source_snapshot_version: `src_${stableSeed(JSON.stringify(sourceFreshness.rows)).toString(16).padStart(8, "0")}`,
    active_components: activeComponents(data),
    generated_at: generatedAt
  };
}

function gradeFromPercent(score: number, maxScore: number): AiSystemGrade {
  const pct = maxScore > 0 ? score / maxScore : 0;
  if (pct >= 0.94) return "A";
  if (pct >= 0.82) return "A-";
  if (pct >= 0.74) return "B+";
  if (pct >= 0.64) return "B";
  if (pct >= 0.58) return "B-";
  if (pct >= 0.52) return "C+";
  if (pct >= 0.46) return "C";
  if (pct >= 0.40) return "C-";
  if (pct >= 0.30) return "D";
  return "F";
}

function factor(
  key: AiAuditFactor["key"],
  label: string,
  score: number,
  maxScore: number,
  status: AiAuditFactor["status"],
  detail: string
): AiAuditFactor {
  const bounded = Math.max(0, Math.min(maxScore, score));
  return {
    key,
    label,
    grade: gradeFromPercent(bounded, maxScore),
    score: Number(bounded.toFixed(1)),
    max_score: maxScore,
    status,
    detail
  };
}

function compactGradeLabel(grade: AiSystemGrade): string {
  return grade;
}

function modelStatesForAudit(data: RuntimeData): Record<string, unknown>[] {
  return gameKeys.flatMap((gameKey) =>
    (["draw_bias", "sales_forecast", "human_pick", "external_factor_audit"] as const).map((modelKey) => modelState(data, gameKey, modelKey))
  );
}

function buildAiAudit(
  data: RuntimeData,
  gamesOut: AdvisorPayload["games"],
  dataHealth: DataHealthSummary,
  sourceFreshness: SourceFreshnessSummary,
  sourceQuality: ReturnType<typeof buildSourceQuality>
): AiSystemAudit {
  const receipts = gamesOut.flatMap((game) => game.ticket.single_ticket_score_receipt ? [game.ticket.single_ticket_score_receipt] : []);
  const minTested = receipts.length ? Math.min(...receipts.map((receipt) => receipt.simulation_field_comparison.tickets_tested)) : 0;
  const avgCoverage = receipts.length
    ? receipts.reduce((sum, receipt) => sum + receipt.simulation_field_comparison.coverage_percent, 0) / receipts.length
    : 0;
  const allGoodField = receipts.length === gameKeys.length && receipts.every((receipt) => receipt.simulation_field_comparison.selected_percentile >= 0.98);
  const fairDrawNumberPolicy =
    receipts.length === gameKeys.length &&
    gamesOut.every((game) => Number(game.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0);
  const searchScore =
    fairDrawNumberPolicy
      ? 18
      : minTested >= 50_000 && allGoodField
      ? 20
      : minTested >= 4_900 && allGoodField
        ? 18
        : minTested >= 1_000
          ? 15
          : minTested >= 100
            ? 9
            : 4;

  const sourceFresh = sourceFreshness.overall_status === "current";
  const sourceUsable = sourceQuality.overall_grade !== "missing" && sourceQuality.overall_grade !== "stale";
  const coreHealthRows = dataHealth.rows.filter((row) => row.kind === "prize" || row.kind === "results" || row.kind === "sync");
  const coreFreshRows = sourceFreshness.rows.filter((row) => row.key.includes(":prize") || row.key.includes(":draws") || row.key === "sync:update");
  const coreMissing = coreHealthRows.some((row) => row.status === "needs_data") || coreFreshRows.some((row) => row.status === "missing");
  const coreWatch = coreHealthRows.some((row) => row.status === "watch") || coreFreshRows.some((row) => row.status === "stale");
  const livePrizeCount = gamesOut.filter((game) => game.data_status.jackpot_source_kind === "live" || game.data_status.jackpot_source_kind === "fixed").length;
  const dataScore =
    sourceFresh && sourceUsable && !coreMissing && !coreWatch
      ? 20
      : sourceUsable && !coreMissing && livePrizeCount === gameKeys.length
        ? 18
        : sourceUsable && !coreMissing
          ? 16
          : sourceUsable
            ? 12
            : 8;

  const states = modelStatesForAudit(data);
  const activeModels = states.filter((state) => Boolean(state.active)).length;
  const gateChecked = states.filter((state) => Boolean(state.reason) || Boolean(state.validation) || Number(state.n ?? 0) > 0).length;
  const heldModels = Math.max(0, states.length - activeModels);
  const learningScore = Math.min(20, 9 + gateChecked * 1.15 + activeModels * 1.5);

  const updateRow = sourceFreshness.rows.find((row) => row.key === "sync:update");
  const automationScore =
    updateRow?.status === "current" && receipts.length === gameKeys.length
      ? 18
      : updateRow?.status === "stale"
        ? 14
        : updateRow
          ? 11
          : 8;

  const allTransparent =
    receipts.length === gameKeys.length &&
    receipts.every(
      (receipt) =>
        receipt.random_baseline_comparison?.jackpot_odds_same &&
        receipt.simulation_field_comparison?.tickets_tested > 0 &&
        receipt.model_contributions.length >= 6
    );
  const transparencyScore = allTransparent ? 18 : receipts.length ? 13 : 7;

  const factors = [
    factor(
      "data",
      "Data foundation",
      dataScore,
      20,
      dataScore >= 18 ? "ready" : dataScore >= 13 ? "building" : "needs_attention",
      dataScore >= 18
        ? "Live/fixed prize inputs, public results, and manual source checks are connected."
        : sourceFresh
          ? "Core source checks are connected, but one source still needs attention."
          : "The system is usable, but source freshness needs another successful update."
    ),
    factor(
      "search",
      fairDrawNumberPolicy ? "Number policy" : "Ticket search",
      searchScore,
      20,
      searchScore >= 18 ? "ready" : searchScore >= 12 ? "building" : "needs_attention",
      fairDrawNumberPolicy
        ? "No validated jackpot-number signal is active, so the app uses fair-draw legal tickets instead of fake search precision."
        : minTested
        ? `${minTested.toLocaleString("en-US")} legal tickets are tested per game before one ticket is chosen.`
        : "No ticket-search receipt was available."
    ),
    factor(
      "learning",
      "Learning gates",
      learningScore,
      20,
      activeModels ? "ready" : gateChecked ? "held" : "building",
      activeModels
        ? `${activeModels} learned component${activeModels === 1 ? "" : "s"} currently pass the evidence gate.`
        : "Learning checks run, but components are held unless they beat the baseline."
    ),
    factor(
      "automation",
      "Manual controls",
      automationScore,
      20,
      automationScore >= 18 ? "ready" : automationScore >= 12 ? "building" : "needs_attention",
      updateRow ? `${updateRow.detail} New checks run only when Sync now is selected.` : "Source and result checks will be recorded after the next manual sync."
    ),
    factor(
      "transparency",
      "Decision receipt",
      transparencyScore,
      20,
      transparencyScore >= 18 ? "ready" : "building",
      allTransparent
        ? "Each ticket carries baseline, source, number-policy, and ignored-signal receipts."
        : "Ticket receipts are present, but one or more explanation fields are still missing."
    )
  ];
  const score = Number(factors.reduce((sum, item) => sum + item.score, 0).toFixed(1));
  const grade = gradeFromPercent(score, 100);
  const label =
    grade === "A" || grade === "A-"
      ? "App process ready"
      : grade === "B+"
        ? "App process usable"
        : grade === "B"
          ? "App process still building"
          : "App needs more source data";
  return {
    grade,
    score,
    max_score: 100,
    label,
    simulation_depth: minTested,
    legal_ticket_space_checked_pct: Number(avgCoverage.toFixed(8)),
    active_learning_models: activeModels,
    held_learning_models: heldModels,
    evidence_gates_checked: gateChecked,
    plain_english:
      `${compactGradeLabel(grade)} process: the app gates learned signals against history, records the reason, and keeps jackpot odds honest. ` +
      "The AI layer improves selection discipline; it does not claim a private way to make random draw odds larger.",
    next_upgrade:
      fairDrawNumberPolicy
        ? "The next real upgrade is more public draw, sales, and winner-count labels so a learned component can earn influence."
        : minTested < 50_000
        ? "The next real upgrade is a dedicated deep-search queue that accumulates larger searches between app opens."
        : activeModels < 3
          ? "The next real upgrade is more sales and winner-count labels so learned components can earn influence."
          : "The next real upgrade is longer live performance history from checked tickets.",
    factors
  };
}

function sourceGradeForGame(data: RuntimeData, gameKey: GameKey): SourceQualityGrade {
  const quality = buildSourceQuality(data);
  const prize = quality.rows.find((row) => row.key === `${gameKey}:prize`);
  const draws = quality.rows.find((row) => row.key === `${gameKey}:draws`);
  if (prize?.grade === "missing" || draws?.grade === "missing") return "missing";
  if (prize?.grade === "stale" || draws?.grade === "stale") return "stale";
  return prize?.grade ?? draws?.grade ?? "incomplete";
}

export function buildSourceFreshness(data: RuntimeData, now = new Date()): SourceFreshnessSummary {
  const rows: SourceFreshnessSummary["rows"] = [];
  for (const gameKey of gameKeys) {
    const game = games[gameKey];
    const snapshot = latestSnapshot(data, gameKey);
    const coverage = data.coverage?.[gameKey];
    const source = snapshot?.source ?? (gameKey === "millionaire_for_life" ? "static_official_prize_chart" : null);
    const label = jackpotSourceLabel(source ?? "default_no_live_snapshot", gameKey);
    const jackpotDays = label.kind === "fixed" ? 0 : daysSinceDate(snapshot?.observed_at, now);
    rows.push({
      key: `${gameKey}:prize`,
      label: `${game.name} prize`,
      status: label.kind === "fallback" ? "missing" : freshnessStatus(jackpotDays, 2, 4),
      detail: label.kind === "fixed" ? "Fixed published top prize." : label.label,
      last_success_at: snapshot?.observed_at ?? null,
      source
    });

    const latestDraw = coverage?.draws.latest ?? null;
    const drawDays = gameKey === "millionaire_for_life" ? 2 : 5;
    rows.push({
      key: `${gameKey}:draws`,
      label: `${game.name} results`,
      status: freshnessStatus(daysSinceDate(latestDraw, now), drawDays, drawDays + 5),
      detail: latestDraw
        ? `${coverage?.draws.n ?? 0} stored results from ${coverage?.draws.first ?? "unknown"} through ${latestDraw}.`
        : "No stored public results yet.",
      last_success_at: latestDraw,
      source: gameKey === "millionaire_for_life" ? "Virginia Lottery" : "State lottery feeds"
    });
  }

  const updateEvent = latestEvent(data, "update_pipeline");
  rows.push({
    key: "sync:update",
    label: "Manual update",
    status: updateEvent ? (updateEvent.status === "failure" ? "stale" : freshnessStatus(daysSinceDate(updateEvent.observed_at, now), 1, 3)) : "stale",
    detail: updateEvent?.detail ?? "No update receipt is stored yet. Sync now will add one.",
    last_success_at: updateEvent?.status === "success" || updateEvent?.status === "warning" ? updateEvent.observed_at : null,
    source: "On-demand sync"
  });

  const overall_status = rows.some((row) => row.status === "missing") ? "missing" : rows.some((row) => row.status === "stale") ? "stale" : "current";
  return {
    generated_at: now.toISOString(),
    overall_status,
    rows
  };
}

function buildGameDataStatus(data: RuntimeData, rec: GameRecommendation): GameDataStatus {
  const game = rec.game_key;
  const snapshot = latestSnapshot(data, game);
  const source = snapshot
    ? snapshot.draw_date < rec.draw_date && game !== "millionaire_for_life"
      ? `stale_snapshot_reused:${snapshot.source}`
      : snapshot.source
    : game === "millionaire_for_life"
      ? "static_official_prize_chart"
      : "default_no_live_snapshot";
  const sourceLabel = jackpotSourceLabel(source, game);
  const coverage = data.coverage?.[game];
  const drawCount = Number(coverage?.draws.n ?? 0);
  const salesCount = Number(coverage?.sales_observations.n ?? 0);
  const tierCount = Number(coverage?.tier_winner_counts.n ?? 0);
  const sourceGrade = sourceGradeForGame(data, game);
  const hasLearningLabels = salesCount >= 60 && tierCount >= 120;
  let confidence = "Prize available; history still building";
  if (sourceLabel.kind === "fallback") confidence = "Prize source needs refresh";
  else if (game === "millionaire_for_life") confidence = drawCount > 0 ? "Fixed prize with early draw history" : "Fixed prize; draw history pending";
  else if (drawCount >= 180) confidence = "Live prize with strong draw history";
  return {
    jackpot_source_label: sourceLabel.label,
    jackpot_source_kind: sourceLabel.kind,
    source_grade: sourceGrade,
    source_grade_label: sourceGradeLabel(sourceGrade),
    jackpot_observed_at: snapshot?.observed_at ?? null,
    jackpot_snapshot_count: Number(coverage?.jackpot_snapshots.n ?? 0),
    jackpot_snapshot_latest_date: coverage?.jackpot_snapshots.latest ?? null,
    draw_history_count: drawCount,
    draw_history_first_date: coverage?.draws.first ?? null,
    latest_draw_date: coverage?.draws.latest ?? null,
    latest_winning_numbers: data.latestDraws?.[game]?.compact ?? null,
    draw_history_source_label:
      drawCount <= 0
        ? "No stored draw history"
        : game === "millionaire_for_life"
          ? "Virginia Lottery history; page carries a manual-entry disclaimer"
          : "State lottery draw history",
    sales_count: salesCount,
    sales_latest_date: coverage?.sales_observations.latest ?? null,
    tier_winner_count: tierCount,
    tier_winner_latest_date: coverage?.tier_winner_counts.latest ?? null,
    confidence_label: confidence,
    learning_status: hasLearningLabels
      ? "Sales and winner-count learning is available."
      : "Payout-sharing estimates need sales and winner-count history."
  };
}

function activeHeld(active: unknown): "active" | "held" {
  return active ? "active" : "held";
}

function buildLearningScope(data: RuntimeData, now = new Date()): LearningScopeSummary {
  return {
    generated_at: now.toISOString(),
    headline: "Recommendations learn from public history first; your tickets only calibrate your private record.",
    personal_scope_note: "Your played-ticket ledger is used to check outcomes and calibrate expectations. It is not treated as evidence that a ball or pattern is more likely.",
    rule_scope_note: "Draw-bias tests use current-rule histories only. Older draws from a different matrix are not mixed into current odds because they were generated under different rules.",
    games: gameKeys.map((gameKey) => {
      const game = games[gameKey];
      const coverage = data.coverage?.[gameKey];
      const drawState = modelState(data, gameKey, "draw_bias");
      const salesState = modelState(data, gameKey, "sales_forecast");
      const crowdState = modelState(data, gameKey, "human_pick");
      const drawCount = Number(coverage?.draws.n ?? 0);
      const salesCount = Number(coverage?.sales_observations.n ?? 0);
      const winnerCount = Number(coverage?.tier_winner_counts.n ?? 0);
      const drawStatus = activeHeld(drawState.active);
      const salesStatus = activeHeld(salesState.active);
      const crowdStatus = activeHeld(crowdState.active);
      return {
        game_key: gameKey,
        game_name: game.name,
        current_rule_start: game.currentRuleStart,
        draw_history_count: drawCount,
        draw_history_first_date: coverage?.draws.first ?? null,
        draw_history_latest_date: coverage?.draws.latest ?? null,
        latest_winning_numbers: data.latestDraws?.[gameKey]?.compact ?? null,
        sales_label_count: salesCount,
        sales_label_latest_date: coverage?.sales_observations.latest ?? null,
        winner_label_count: winnerCount,
        winner_label_latest_date: coverage?.tier_winner_counts.latest ?? null,
        draw_bias_status: drawStatus,
        sales_model_status: salesStatus,
        crowd_model_status: crowdStatus,
        plain_english:
          `Used ${drawCount.toLocaleString("en-US")} current-rule draws, ` +
          `${salesCount.toLocaleString("en-US")} sales labels, and ${winnerCount.toLocaleString("en-US")} winner-count labels. ` +
          `Draw-bias is ${drawStatus}; sales learning is ${salesStatus}; crowd/split learning is ${crowdStatus}.`
      };
    })
  };
}

function pctLabel(value: unknown): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
}

function externalStatus(active: unknown, n: number, min = 40): "active" | "held" | "building" {
  if (active) return "active";
  return n >= min ? "held" : "building";
}

function bestExternalFactorLabel(state: Record<string, unknown>, salesState: Record<string, unknown>): string {
  const groups = Array.isArray(salesState.feature_groups) ? (salesState.feature_groups as Array<Record<string, unknown>>) : [];
  const bestGroup = groups
    .filter((row) => row.usable_for_recommendation !== false && Number.isFinite(Number(row.improvement_pct)))
    .sort((a, b) => Number(b.improvement_pct ?? 0) - Number(a.improvement_pct ?? 0))[0];
  if (bestGroup && Number(bestGroup.improvement_pct ?? 0) > 0) return `${String(bestGroup.label ?? bestGroup.group ?? "Factor")} ${pctLabel(bestGroup.improvement_pct)}`;
  const outsideGroups = Array.isArray(state.factor_groups) ? (state.factor_groups as Array<Record<string, unknown>>) : [];
  const bestOutside = outsideGroups
    .filter((row) => row.usable_for_recommendation !== false && Number.isFinite(Number(row.score_pct)))
    .sort((a, b) => Number(b.score_pct ?? 0) - Number(a.score_pct ?? 0))[0];
  if (bestOutside && Number(bestOutside.score_pct ?? 0) > 0) return `${String(bestOutside.label ?? bestOutside.group ?? "Factor")} ${pctLabel(bestOutside.score_pct)}`;
  return "No outside factor earned influence";
}

function buildOutsideSignalAudit(data: RuntimeData, now = new Date()): OutsideSignalAuditSummary {
  return {
    generated_at: now.toISOString(),
    headline: "Outside factors are tested, but only proven signals get weight.",
    principle: "Weather, holidays, calendar timing, and jackpot context must beat a time-split baseline before they can affect value, sales, split risk, or number choice.",
    games: gameKeys.map((gameKey) => {
      const game = games[gameKey];
      const state = modelState(data, gameKey, "external_factor_audit");
      const salesState = modelState(data, gameKey, "sales_forecast");
      const number = (state.number_signal ?? {}) as Record<string, unknown>;
      const split = (state.split_signal ?? {}) as Record<string, unknown>;
      const testedDraws = Number(state.draw_n ?? number.n ?? 0);
      const testedSales = Number(salesState.n ?? state.sales_n ?? 0);
      const testedSplit = Number(split.n ?? 0);
      const numberStatus = externalStatus(number.active, testedDraws, 120);
      const economicsStatus = externalStatus(salesState.active, testedSales, 60);
      const splitStatus = externalStatus(split.active, testedSplit, 80);
      return {
        game_key: gameKey,
        game_name: game.name,
        number_status: numberStatus,
        economics_status: economicsStatus,
        split_status: splitStatus,
        tested_draws: testedDraws,
        tested_sales: testedSales,
        tested_split_rows: testedSplit,
        best_factor_label: bestExternalFactorLabel(state, salesState),
        number_detail:
          numberStatus === "active"
            ? `Outside factors passed the ball-shape gate by ${pctLabel(number.avg_improvement_pct)}.`
            : numberStatus === "held"
              ? `Tested against ${testedDraws.toLocaleString("en-US")} draws; held from number choice because it did not beat fair draw strongly enough.`
              : "Needs more current-rule draws before outside factors can be judged for number choice.",
        economics_detail:
          economicsStatus === "active"
            ? `Allowed for economics after beating the sales baseline by ${pctLabel(salesState.improvement_pct)}.`
            : economicsStatus === "held"
              ? `Tested on ${testedSales.toLocaleString("en-US")} sales labels; held unless it beats the temporal baseline.`
              : "Needs more sales labels before outside factors can move game value.",
        split_detail:
          splitStatus === "active"
            ? `Allowed for split-risk context after beating the residual baseline by ${pctLabel(split.improvement_pct)}.`
            : splitStatus === "held"
              ? `Tested on ${testedSplit.toLocaleString("en-US")} joined winner/sales rows; held from payout sharing.`
              : "Needs joined winner-count and sales labels before outside factors can affect split-risk."
      };
    })
  };
}

export function noSplitBreakEvenCash(gameKey: GameKey, taxKeepRate: number, usePowerplay = false): number {
  const game = games[gameKey];
  const lowerEv = lowerTierEv(game, taxKeepRate, usePowerplay);
  const cost = ticketCost(game, usePowerplay);
  if (lowerEv >= cost) return 0;
  return (cost - lowerEv) / (jackpotProbability(game) * Math.max(taxKeepRate, 1e-12));
}

function drawProbabilityForTicket(data: RuntimeData, gameKey: GameKey, ticket: Ticket, mode: RecommendationConfig["draw_bias_mode"]): { probability: number; weight: number } {
  const game = games[gameKey];
  const uniform = 1 / totalCombinations(game);
  if (mode === "off") return { probability: uniform, weight: 0 };
  const state = modelState(data, gameKey, "draw_bias");
  let weight = Number(state.weight ?? 0);
  if (mode === "auto" && !state.active) return { probability: uniform, weight: 0 };
  if (mode === "force" && weight <= 0) weight = 0.03;
  if (weight <= 0) return { probability: uniform, weight: 0 };
  const whiteProbs = Array.isArray(state.white_probs) ? (state.white_probs as number[]) : [];
  const bonusProbs = Array.isArray(state.bonus_probs) ? (state.bonus_probs as number[]) : [];
  if (whiteProbs.length <= game.whiteMax || bonusProbs.length <= game.bonusMax) return { probability: uniform, weight: 0 };
  let p = 120;
  for (const white of ticket.whites) p *= Math.max(Number(whiteProbs[white]), 1e-12);
  p *= Math.max(Number(bonusProbs[ticket.bonus]), 1e-12);
  return { probability: Math.max((1 - weight) * uniform + weight * p, 1e-18), weight };
}

function makeDrawFeatures(drawDate: string, jackpotCash: number): Record<string, number> {
  const d = parseIsoDate(drawDate);
  const dow = weekdayMon0(d);
  return {
    bias: 1,
    log_jackpot_cash: Math.log(Math.max(jackpotCash, 1)),
    day_sin: Math.sin((2 * Math.PI * dow) / 7),
    day_cos: Math.cos((2 * Math.PI * dow) / 7),
    is_us_holiday: 0,
    days_to_nearest_holiday_inv: 0,
    avg_temp_mean: 0,
    avg_precip_sum: 0,
    avg_wind_max: 0
  };
}

function featureVector(features: Record<string, number>, columns: string[]): number[] {
  return columns.map((column) => Number(features[column] ?? 0));
}

export function estimateSalesHeuristic(gameKey: GameKey, jackpotCash: number, drawDate: string): number {
  const base = DEFAULT_SALES[gameKey];
  const cash = Math.max(jackpotCash, 1);
  if (gameKey === "millionaire_for_life") return base;
  const d = parseIsoDate(drawDate);
  let scale = 1 + 0.22 * Math.max(0, Math.log(cash / 50_000_000));
  if (cash > 500_000_000) scale += 0.4;
  if ([4, 5].includes(weekdayMon0(d))) scale += 0.04;
  return Math.max(base * scale, base * 0.5);
}

export function estimateSalesFromState(data: RuntimeData, gameKey: GameKey, drawDate: string, jackpotCash: number): { sales: number; status: string } {
  const state = modelState(data, gameKey, "sales_forecast");
  const columns = Array.isArray(state.columns) ? (state.columns as string[]) : [];
  const coefficients = Array.isArray(state.coefficients) ? (state.coefficients as number[]) : [];
  if (state.active && columns.length && columns.length === coefficients.length) {
    const xs = featureVector(makeDrawFeatures(drawDate, jackpotCash), columns);
    const predLog = xs.reduce((sum, value, idx) => sum + Number(coefficients[idx]) * value, 0);
    return { sales: Math.max(Math.exp(predLog), 1_000), status: "validated_sales_model" };
  }
  return { sales: estimateSalesHeuristic(gameKey, jackpotCash, drawDate), status: "heuristic_no_validated_sales_labels" };
}

function learnedHumanLogAdjustment(data: RuntimeData, gameKey: GameKey, ticket: Ticket): number {
  const state = modelState(data, gameKey, "human_pick");
  if (!state.active) return 0;
  const columns = Array.isArray(state.columns) ? (state.columns as string[]) : [];
  const coefficients = Array.isArray(state.coefficients) ? (state.coefficients as number[]) : [];
  if (!columns.length || columns.length !== coefficients.length) return 0;
  const feats = ticketFeatures(ticket, games[gameKey]);
  const raw = humanFeatureVector(feats, columns).reduce((sum, value, idx) => sum + Number(coefficients[idx]) * value, 0);
  return Math.max(Math.min(raw, 1.25), -1.25);
}

function opportunityScore(rawValue: number, roi: number, chances: ReturnType<typeof prizeChanceSummary>, jackpotCash: number, cost: number): number {
  const valueComponent = roi * 100;
  const jackpotComponent = Math.log10(Math.max(jackpotCash / Math.max(cost, 1), 1)) * 1.8;
  const meaningfulPrizeComponent = chances.meaningful_prize_probability * 2_500;
  const positiveValueBonus = rawValue > 0 ? 40 : 0;
  return valueComponent + jackpotComponent + meaningfulPrizeComponent + positiveValueBonus;
}

function opportunityReason(rec: Pick<GameRecommendation, "ev_per_ticket_uniform_public" | "roi_per_dollar" | "any_prize_probability" | "meaningful_prize_probability">): string {
  if (rec.ev_per_ticket_uniform_public > 0) return "Best value after cost.";
  if (rec.meaningful_prize_probability > 0.00001) return "Best optional play today.";
  return "Best mix of prize, cost, and odds.";
}

function emptyPersonalCalibrationSummary(data: RuntimeData): PersonalCalibrationSummary {
  return {
    tracked_tickets: data.registry?.length ?? 0,
    checked_tickets: 0,
    prize_tickets: 0,
    expected_return: 0,
    actual_return: 0,
    expected_any_prizes: 0,
    actual_prize_rate: 0,
    normal_low_prize_tickets: 0,
    normal_high_prize_tickets: 0,
    sample_note: "No checked tickets yet."
  };
}

function patternSanityPenalty(game: GameConfig, ticket: Ticket): { penalty: number; label: string; flags: number } {
  const f = ticketFeatures(ticket, game);
  const range = Number(f.range ?? 0);
  const narrowRange = range < game.whiteMax * 0.18;
  const extremeRange = range > game.whiteMax * 0.82;
  let penalty = 0;
  let flags = 0;
  if (f.arithmetic_sequence) {
    penalty += 0.045;
    flags += 1;
  }
  if (f.consecutive_pairs >= 3) {
    penalty += 0.035;
    flags += 1;
  } else if (f.consecutive_pairs >= 2) {
    penalty += 0.015;
    flags += 1;
  }
  if (f.all_low_1_31) {
    penalty += 0.025;
    flags += 1;
  }
  if (f.high_count === game.whitePick) {
    penalty += 0.015;
    flags += 1;
  }
  if (f.same_decade_heavy) {
    penalty += 0.012;
    flags += 1;
  }
  if (f.same_last_digit_pairs >= 2) {
    penalty += 0.01;
    flags += 1;
  }
  if (narrowRange) {
    penalty += 0.02;
    flags += 1;
  }
  if (extremeRange) {
    penalty += 0.006;
    flags += 1;
  }
  const capped = Math.min(penalty, 0.08);
  return {
    penalty: capped,
    label: capped > 0.03 ? "Guarded" : capped > 0 ? "Checked" : "Neutral",
    flags
  };
}

function ticketFromRegistryCompact(value: string): Ticket | null {
  const compact = value.split(":").at(-1) ?? value;
  const match = compact.match(/^(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})\+(\d{2})$/);
  if (!match) return null;
  return createTicket(match.slice(1, 6).map(Number), Number(match[6]));
}

function rotationSimilarityPenalty(data: RuntimeData, gameKey: GameKey, ticket: Ticket): { penalty: number; label: string; maxOverlap: number } {
  let maxOverlap = 0;
  for (const key of data.registry ?? []) {
    if (!key.startsWith(`${gameKey}:`)) continue;
    const prior = ticketFromRegistryCompact(key);
    if (!prior) continue;
    maxOverlap = Math.max(maxOverlap, ticketOverlap(ticket, prior));
  }
  const penalty = maxOverlap >= 5 ? 0.08 : maxOverlap >= 4 ? 0.04 : maxOverlap >= 3 ? 0.012 : 0;
  return {
    penalty,
    label: maxOverlap >= 4 ? "Rotated" : maxOverlap >= 3 ? "Checked" : "Fresh",
    maxOverlap
  };
}

function modelContributionLines(
  data: RuntimeData,
  gameKey: GameKey,
  ticket: TicketScore,
  recScore: number,
  config: RecommendationConfig,
  dataHealthStatus: NonNullable<TicketScore["single_ticket_score_receipt"]>["data_health_status"]
): ModelContributionLine[] {
  const draw = modelState(data, gameKey, "draw_bias");
  const sales = modelState(data, gameKey, "sales_forecast");
  const crowd = modelState(data, gameKey, "human_pick");
  const patternPenalty = Number(ticket.feature_summary.pattern_sanity_penalty ?? 0);
  const rotationPenalty = Number(ticket.feature_summary.rotation_similarity_penalty ?? 0);
  const jackpotOnly = config.selection_goal === "jackpot_first";
  return [
    {
      component: "fair_draw",
      status: draw.active ? "active" : "baseline",
      influence: Number(draw.weight ?? 0),
      label: draw.active ? "Past-number signal used" : "Fair odds baseline",
      detail: draw.active ? "Historical draw test passed the evidence gate." : "Past numbers did not prove a better-than-fair draw model."
    },
    {
      component: "cash_value",
      status: jackpotOnly ? "baseline" : "active",
      influence: Math.max(0, Math.min(1, Math.abs(recScore) / 100)),
      label: jackpotOnly ? "Value shown separately" : "Cash value check",
      detail: jackpotOnly
        ? "Used to rank games and show economics; not used to make one number combination more likely."
        : "Cash prize, tax setting, lower prizes, ticket cost, and sharing value drive the ranking."
    },
    {
      component: "sales",
      status: sales.active ? "active" : "held",
      influence: sales.active ? Math.max(0, Math.min(1, Number(sales.improvement_pct ?? 0) / 100)) : 0,
      label: sales.active ? "Sales model used" : "Sales learning held",
      detail: sales.active
        ? jackpotOnly
          ? "Historical sales labels beat the baseline for game economics; not for ball prediction."
          : "Historical sales labels beat the baseline."
        : String(sales.reason ?? "Needs historical sales labels before it can move tickets.")
    },
    {
      component: "crowd",
      status: crowd.active ? "active" : "held",
      influence: crowd.active ? Math.max(0, Math.min(1, Number(crowd.weight ?? 0))) : 0,
      label: crowd.active ? "Crowd behavior used" : "Crowd learning held",
      detail: crowd.active ? "Winner-count residuals passed validation." : String(crowd.reason ?? "Needs joined sales and winner-count labels.")
    },
    {
      component: "pattern_guard",
      status: patternPenalty > 0 ? "guardrail" : "baseline",
      influence: patternPenalty,
      label: patternPenalty > 0 ? "Pattern guard applied" : "Pattern neutral",
      detail: jackpotOnly ? "Not used for jackpot-first number selection." : "This only breaks ties against absurd-looking tickets; it does not change drawing odds."
    },
    {
      component: "rotation",
      status: rotationPenalty > 0 ? "guardrail" : "baseline",
      influence: rotationPenalty,
      label: rotationPenalty > 0 ? "Rotation guard applied" : "Fresh rotation",
      detail: jackpotOnly ? "Not used for jackpot-first number selection." : "Exact repeats are blocked and near-repeats are lightly penalized for one-ticket play."
    },
    {
      component: "source_health",
      status: dataHealthStatus === "good" ? "active" : "guardrail",
      influence: dataHealthStatus === "good" ? 1 : 0.4,
      label: dataHealthStatus === "good" ? "Data ready" : "Data still building",
      detail: "Source health controls whether learned components are trusted."
    },
    {
      component: "personal_record",
      status: "baseline",
      influence: 0,
      label: "Personal calibration",
      detail: "Your result history calibrates expectations; it does not create a private draw signal."
    }
  ];
}

function singleTicketScoreReceipt(
  data: RuntimeData,
  gameKey: GameKey,
  ticket: TicketScore,
  chances: ReturnType<typeof prizeChanceSummary>,
  gameRankScore: number,
  config: RecommendationConfig,
  jackpotCash: number,
  estimatedSales: number,
  drawDate: string,
  poolStats: CandidatePoolStats,
  selectedRank: number,
  dataHealthStatus: NonNullable<TicketScore["single_ticket_score_receipt"]>["data_health_status"]
): NonNullable<TicketScore["single_ticket_score_receipt"]> {
  const sourceGrade = sourceGradeForGame(data, gameKey);
  const game = games[gameKey];
  const baselineRng = mulberry32(stableSeed(config.user_id, gameKey, "random-baseline", rotationId(drawDate, config.rotation_mode), ticket.ticket.compact));
  const baselineTicket = randomTicket(game, baselineRng, false);
  const baseline = scoreTicket(data, gameKey, baselineTicket, jackpotCash, estimatedSales, config);
  const valueDelta = ticket.raw_ev - baseline.raw_ev;
  const scoreDelta = ticket.score - baseline.score;
  const publicPickDelta = ticket.popularity_multiplier - baseline.popularity_multiplier;
  const sharingDelta = ticket.split_share_factor - baseline.split_share_factor;
  const verdict = scoreDelta > 0.02 ? "stronger" : scoreDelta < -0.02 ? "weaker" : "similar";
  const tested = Math.max(1, poolStats.ticketsTested);
  const rank = Math.max(1, Math.min(selectedRank || 1, tested));
  const selectedPercentile = Math.max(0, Math.min(1, (tested - rank + 1) / tested));
  const coveragePercent = Math.max(0, Math.min(1, tested / Math.max(poolStats.legalTicketSpace, 1)));
  const scoreLift = ticket.score - poolStats.medianScore;
  const valueLift = ticket.raw_ev - poolStats.medianAfterTaxValue;
  const noBiasJackpot = Number(ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0;
  const confidenceLabel =
    noBiasJackpot
      ? "Fair-draw receipt"
      : tested >= 50_000 && selectedPercentile >= 0.995
      ? "High process confidence"
      : tested >= 4_900 && selectedPercentile >= 0.98
        ? "Good process confidence"
        : "Limited process confidence";
  const notes = [
    "One private-player ticket.",
    "No low/high pattern is forced.",
    "Payout risk affects value, not draw odds.",
    "Pattern guard is a tie-breaker, not a draw-odds claim."
  ];
  if (ticket.feature_summary.forced_shape_adjustment === 0) notes.push("No shape rule applied.");
  if (Number(ticket.feature_summary.pattern_sanity_penalty ?? 0) <= 0) notes.push("Pattern guard passed.");
  if (Number(ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0) {
    notes.push("No validated jackpot-number signal is active; this is an unbiased legal ticket for the draw.");
  }
  return {
    advisor_mode: "single_player",
    receipt_version: "single_player_v12",
    game_rank_score: gameRankScore,
    candidate_search_count: tested,
    selection_goal: config.selection_goal,
    after_tax_value: ticket.raw_ev,
    physical_draw_odds: ticket.draw_probability,
    top_prize_odds: ticket.draw_probability,
    any_prize_probability: chances.any_prize_probability,
    meaningful_prize_probability: chances.meaningful_prize_probability,
    jackpot_value_after_tax: jackpotCash * config.tax_keep_rate * ticket.split_share_factor,
    lower_prize_value_after_tax: ticket.lower_ev,
    ticket_cost: ticket.cost,
    source_quality_grade: sourceGrade,
    source_snapshot_status: sourceGrade,
    data_health_status: dataHealthStatus,
    payout_sharing_factor: ticket.split_share_factor,
    public_pick_multiplier: ticket.popularity_multiplier,
    public_pick_probability: ticket.public_pick_probability,
    expected_split_lambda: ticket.split_lambda,
    pattern_sanity_label: String(ticket.feature_summary.pattern_sanity_label_code ?? 0) === "2" ? "Guarded" : String(ticket.feature_summary.pattern_sanity_label_code ?? 0) === "1" ? "Checked" : "Neutral",
    pattern_sanity_penalty: Number(ticket.feature_summary.pattern_sanity_penalty ?? 0),
    rotation_similarity_label: String(ticket.feature_summary.rotation_similarity_label_code ?? 0) === "2" ? "Rotated" : String(ticket.feature_summary.rotation_similarity_label_code ?? 0) === "1" ? "Checked" : "Fresh",
    rotation_similarity_penalty: Number(ticket.feature_summary.rotation_similarity_penalty ?? 0),
    random_baseline_comparison: {
      ticket: baseline.ticket,
      after_tax_value: baseline.raw_ev,
      score: baseline.score,
      public_pick_multiplier: baseline.popularity_multiplier,
      payout_sharing_factor: baseline.split_share_factor,
      expected_split_lambda: baseline.split_lambda,
      value_delta: valueDelta,
      score_delta: scoreDelta,
      public_pick_multiplier_delta: publicPickDelta,
      payout_sharing_delta: sharingDelta,
      jackpot_odds_same: true,
      verdict,
      plain_english:
        verdict === "stronger"
          ? "Same jackpot odds as the random baseline, with a better one-ticket profile."
          : verdict === "weaker"
            ? "Same jackpot odds as the random baseline; kept because today's full scoring favored this ticket."
            : "Same jackpot odds and a similar profile to the random baseline."
    },
    simulation_field_comparison: {
      legal_ticket_space: poolStats.legalTicketSpace,
      tickets_tested: tested,
      search_attempts: poolStats.searchAttempts,
      coverage_percent: coveragePercent,
      selected_rank: rank,
      selected_percentile: selectedPercentile,
      selected_score: ticket.score,
      median_score: poolStats.medianScore,
      selected_score_lift: scoreLift,
      selected_after_tax_value: ticket.raw_ev,
      median_after_tax_value: poolStats.medianAfterTaxValue,
      selected_value_lift: valueLift,
      confidence_label: confidenceLabel,
      plain_english: noBiasJackpot
        ? "No validated jackpot-number signal is active, so the selected numbers are a fair-draw legal ticket for this draw."
        : `${tested.toLocaleString("en-US")} legal tickets were tested for this game today. This ticket ranked #${rank.toLocaleString("en-US")} in that field.`
    },
    model_contributions: modelContributionLines(data, gameKey, ticket, gameRankScore, config, dataHealthStatus),
    personal_record_checked_tickets: 0,
    selection_notes: notes
  };
}

export function scoreTicket(
  data: RuntimeData,
  gameKey: GameKey,
  ticket: Ticket,
  jackpotCash: number,
  estimatedSales: number,
  config: RecommendationConfig
): TicketScore {
  const game = games[gameKey];
  validateTicket(game, ticket);
  const draw = drawProbabilityForTicket(data, gameKey, ticket, config.draw_bias_mode);
  const humanState = modelState(data, gameKey, "human_pick");
  let publicPick = publicPickProbability(ticket, game, config.quick_pick_share, humanState);
  const learnedLog = learnedHumanLogAdjustment(data, gameKey, ticket);
  if (learnedLog) {
    const uniformPublic = 1 / totalCombinations(game);
    const multiplier = publicPick.multiplier * safeExp(learnedLog);
    publicPick = {
      probability: config.quick_pick_share * uniformPublic + (1 - config.quick_pick_share) * uniformPublic * multiplier,
      multiplier,
      features: { ...publicPick.features, learned_human_log_adjustment: learnedLog }
    };
  }
  const lambda = estimatedSales * publicPick.probability;
  const share = poissonSplitShareFactor(lambda);
  const lowerEv = lowerTierEv(game, config.tax_keep_rate, config.use_powerplay);
  const jackpotEv = draw.probability * jackpotCash * config.tax_keep_rate * share;
  const cost = ticketCost(game, config.use_powerplay);
  const rawEv = lowerEv + jackpotEv - cost;
  const sanity = patternSanityPenalty(game, ticket);
  const rotation = rotationSimilarityPenalty(data, gameKey, ticket);
  const jackpotFirstEv = lowerEv + draw.probability * jackpotCash * config.tax_keep_rate * Math.sqrt(Math.max(share, 0)) - cost;
  const baseScore = config.selection_goal === "value_first" ? rawEv : jackpotFirstEv;
  const jackpotOnlySelection = config.selection_goal === "jackpot_first";
  const score = jackpotOnlySelection
    ? draw.probability
    : baseScore - config.risk_aversion * Math.max(0, cost - lowerEv) - sanity.penalty - rotation.penalty;
  return {
    ticket: toPayload(ticket),
    raw_ev: rawEv,
    jackpot_ev: jackpotEv,
    lower_ev: lowerEv,
    cost,
    popularity_multiplier: publicPick.multiplier,
    public_pick_probability: publicPick.probability,
    split_lambda: lambda,
    split_share_factor: share,
    draw_probability: draw.probability,
    score,
    feature_summary: {
      ...publicPick.features,
      forced_shape_adjustment: 0,
      pattern_sanity_penalty: jackpotOnlySelection ? 0 : sanity.penalty,
      pattern_sanity_flags: jackpotOnlySelection ? 0 : sanity.flags,
      pattern_sanity_label_code: jackpotOnlySelection ? 0 : sanity.label === "Guarded" ? 2 : sanity.label === "Checked" ? 1 : 0,
      rotation_similarity_penalty: jackpotOnlySelection ? 0 : rotation.penalty,
      rotation_similarity_max_overlap: rotation.maxOverlap,
      rotation_similarity_label_code: jackpotOnlySelection ? 0 : rotation.label === "Rotated" ? 2 : rotation.label === "Checked" ? 1 : 0,
      jackpot_first_no_bias_selection: jackpotOnlySelection && draw.weight <= 0 ? 1 : 0
    }
  };
}

function rotationId(drawDate: string, mode: RecommendationConfig["rotation_mode"]): string {
  if (mode === "static") return "static";
  if (mode === "week") return weekId(parseIsoDate(drawDate));
  return drawDate;
}

export function candidateTicketForStrategy(game: GameConfig, rng: () => number): Ticket {
  const roll = rng();
  if (roll < 0.66) return randomTicket(game, rng, false);
  if (roll < 0.84) return diversityTicket(game, rng);
  return antiPopularityTicket(game, rng);
}

function weightedPickWithoutReplacement(items: number[], weights: number[], count: number, rng: () => number): number[] {
  const pool = [...items];
  const ws = [...weights];
  const out: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const total = ws.reduce((sum, value) => sum + Math.max(value, 1e-12), 0);
    let roll = rng() * total;
    let idx = 0;
    for (; idx < ws.length - 1; idx += 1) {
      roll -= Math.max(ws[idx], 1e-12);
      if (roll <= 0) break;
    }
    out.push(pool[idx]);
    pool.splice(idx, 1);
    ws.splice(idx, 1);
  }
  return out.sort((a, b) => a - b);
}

function drawSignalTicket(data: RuntimeData, gameKey: GameKey, rng: () => number): Ticket | null {
  const state = modelState(data, gameKey, "draw_bias");
  if (!state.active || Number(state.weight ?? 0) <= 0) return null;
  const game = games[gameKey];
  const whiteProbs = Array.isArray(state.white_probs) ? (state.white_probs as number[]) : [];
  const bonusProbs = Array.isArray(state.bonus_probs) ? (state.bonus_probs as number[]) : [];
  if (whiteProbs.length <= game.whiteMax || bonusProbs.length <= game.bonusMax) return null;
  const whites = weightedPickWithoutReplacement(
    Array.from({ length: game.whiteMax }, (_, i) => i + 1),
    Array.from({ length: game.whiteMax }, (_, i) => Number(whiteProbs[i + 1] ?? 0)),
    game.whitePick,
    rng
  );
  const bonus = weightedPickWithoutReplacement(
    Array.from({ length: game.bonusMax }, (_, i) => i + 1),
    Array.from({ length: game.bonusMax }, (_, i) => Number(bonusProbs[i + 1] ?? 0)),
    1,
    rng
  )[0];
  return createTicket(whites, bonus);
}

function hasUsableDrawSignal(data: RuntimeData, gameKey: GameKey, mode: RecommendationConfig["draw_bias_mode"]): boolean {
  if (mode === "off") return false;
  const state = modelState(data, gameKey, "draw_bias");
  if (mode === "auto" && !state.active) return false;
  let weight = Number(state.weight ?? 0);
  if (mode === "force" && weight <= 0) weight = 0.03;
  if (weight <= 0) return false;
  const game = games[gameKey];
  const whiteProbs = Array.isArray(state.white_probs) ? (state.white_probs as number[]) : [];
  const bonusProbs = Array.isArray(state.bonus_probs) ? (state.bonus_probs as number[]) : [];
  return whiteProbs.length > game.whiteMax && bonusProbs.length > game.bonusMax;
}

function candidateTicketForRecommendation(data: RuntimeData, gameKey: GameKey, rng: () => number, config: RecommendationConfig): Ticket {
  if (config.selection_goal === "jackpot_first") {
    const signalTicket = hasUsableDrawSignal(data, gameKey, config.draw_bias_mode) && rng() < 0.7 ? drawSignalTicket(data, gameKey, rng) : null;
    return signalTicket ?? randomTicket(games[gameKey], rng, false);
  }
  const signalTicket = rng() < 0.35 ? drawSignalTicket(data, gameKey, rng) : null;
  return signalTicket ?? candidateTicketForStrategy(games[gameKey], rng);
}

function buildCandidatePool(
  data: RuntimeData,
  gameKey: GameKey,
  jackpotCash: number,
  estimatedSales: number,
  config: RecommendationConfig,
  drawDate: string
): CandidatePoolResult {
  const game = games[gameKey];
  const seed = stableSeed(config.user_id, gameKey, rotationId(drawDate, config.rotation_mode), config.random_seed_extra);
  const rng = mulberry32(seed);
  const seen = new Set<string>();
  const registry = new Set(data.registry ?? []);
  const scored: TicketScore[] = [];
  const noBiasJackpot = config.selection_goal === "jackpot_first" && !hasUsableDrawSignal(data, gameKey, config.draw_bias_mode);
  if (noBiasJackpot) {
    let attempts = 0;
    let selectedTicket: Ticket | null = null;
    while (attempts < 64 && !selectedTicket) {
      attempts += 1;
      const ticket = randomTicket(game, rng, false);
      const compact = compactTicket(ticket);
      if (!registry.has(`${gameKey}:${drawDate}:${compact}`)) selectedTicket = ticket;
    }
    selectedTicket ??= randomTicket(game, rng, false);
    const ticket = scoreTicket(data, gameKey, selectedTicket, jackpotCash, estimatedSales, config);
    return {
      tickets: [ticket],
      stats: {
        legalTicketSpace: totalCombinations(game),
        ticketsTested: 1,
        searchAttempts: Math.max(1, attempts),
        medianScore: ticket.score,
        medianAfterTaxValue: ticket.raw_ev
      }
    };
  }
  const target = Math.max(config.candidate_count, config.keep_top);
  let attempts = 0;
  while (seen.size < target && attempts < target * 4) {
    attempts += 1;
    const ticket = candidateTicketForRecommendation(data, gameKey, rng, config);
    const compact = compactTicket(ticket);
    if (seen.has(compact)) continue;
    seen.add(compact);
    if (registry.has(`${gameKey}:${drawDate}:${compact}`)) continue;
    scored.push(scoreTicket(data, gameKey, ticket, jackpotCash, estimatedSales, config));
  }
  scored.sort((a, b) => b.score - a.score);
  const scoreValues = scored.map((item) => item.score).sort((a, b) => a - b);
  const valueValues = scored.map((item) => item.raw_ev).sort((a, b) => a - b);
  const mid = Math.floor(scoreValues.length / 2);
  const medianScore = scoreValues.length ? scoreValues[mid] : 0;
  const medianAfterTaxValue = valueValues.length ? valueValues[mid] : 0;
  return {
    tickets: scored.slice(0, config.keep_top),
    stats: {
      legalTicketSpace: totalCombinations(game),
      ticketsTested: scored.length,
      searchAttempts: attempts,
      medianScore,
      medianAfterTaxValue
    }
  };
}

function incrementalPortfolioScore(selected: TicketScore[], candidate: TicketScore, maxOverlap: number): number | null {
  let overlapPenalty = 0;
  for (const chosen of selected) {
    const overlap = ticketOverlap(candidate.ticket, chosen.ticket);
    if (overlap > maxOverlap) return null;
    overlapPenalty += 0.06 * overlap ** 2;
  }
  return candidate.score + coverageBonus(selected.map((item) => item.ticket), candidate.ticket) - overlapPenalty;
}

function portfolioObjective(selected: TicketScore[], maxOverlap: number): number {
  let score = 0;
  const running: TicketScore[] = [];
  for (const ticket of selected) {
    const inc = incrementalPortfolioScore(running, ticket, maxOverlap);
    if (inc === null) return -1e99;
    score += inc;
    running.push(ticket);
  }
  return score;
}

export function optimizePortfolio(candidates: TicketScore[], playCount: number, maxOverlap = 2): TicketScore[] {
  const selected: TicketScore[] = [];
  let remaining = [...candidates];
  for (let i = 0; i < playCount; i += 1) {
    let best: TicketScore | null = null;
    let bestScore = -1e99;
    for (const candidate of remaining) {
      const adjusted = incrementalPortfolioScore(selected, candidate, maxOverlap);
      if (adjusted === null) continue;
      if (adjusted > bestScore) {
        bestScore = adjusted;
        best = candidate;
      }
    }
    if (!best) break;
    selected.push(best);
    remaining = remaining.filter((item) => item.ticket.compact !== best?.ticket.compact);
  }
  if (!selected.length || !remaining.length) return selected;
  let current = portfolioObjective(selected, maxOverlap);
  for (let pass = 0; pass < 2; pass += 1) {
    let improved = false;
    for (let i = 0; i < selected.length; i += 1) {
      const old = selected[i];
      for (const candidate of remaining.slice(0, 2_000)) {
        const trial = [...selected];
        trial[i] = candidate;
        const objective = portfolioObjective(trial, maxOverlap);
        if (objective > current + 1e-9) {
          selected[i] = candidate;
          remaining = remaining.filter((item) => item.ticket.compact !== candidate.ticket.compact).concat(old);
          current = objective;
          improved = true;
          break;
        }
      }
      if (improved) break;
    }
    if (!improved) break;
  }
  return selected;
}

function attachGameDiagnostics(data: RuntimeData, rec: GameRecommendation, taxKeepRate: number, usePowerplay: boolean): Record<string, unknown> {
  const game = games[rec.game_key];
  const uniformShare = poissonSplitShareFactor(rec.estimated_sales / totalCombinations(game));
  const denom = jackpotProbability(game) * Math.max(taxKeepRate, 1e-12) * Math.max(uniformShare, 1e-12);
  const needed = ticketCost(game, usePowerplay) - rec.lower_tier_ev;
  const breakEvenSplit = needed <= 0 ? 0 : needed / denom;
  const playCount = rec.tickets.length;
  const portfolioJp = playCount > 0 ? 1 - (1 - jackpotProbability(game)) ** playCount : 0;
  const tierProbs = game.lowerTiers.reduce((sum, tier) => sum + matchProbability(game, tier.whiteHits, tier.bonusHit), 0) + jackpotProbability(game);
  const portfolioAnyPrize = playCount > 0 ? 1 - (1 - tierProbs) ** playCount : 0;
  const modelStates: Record<string, unknown> = {};
  for (const key of ["draw_bias", "sales_forecast", "human_pick"] as const) {
    const state = modelState(data, rec.game_key, key);
    modelStates[key] = {
      active: Boolean(state.active),
      reason: state.reason,
      validation: state.validation,
      n: state.n,
      test_n: state.test_n,
      improvement_pct: state.improvement_pct,
      z_score: state.z_score,
      p_value: state.p_value
    };
  }
  return {
    break_even_cash_jackpot_uniform_public: breakEvenSplit,
    portfolio_jackpot_probability_uniform: portfolioJp,
    portfolio_any_prize_probability_approx: portfolioAnyPrize,
    model_states: modelStates,
    jackpot_odds_denominator: totalCombinations(game),
    top_prize_probability: jackpotProbability(game),
    uniform_split_share_factor: uniformShare
  };
}

export function recommendForGameAt(
  data: RuntimeData,
  gameKey: GameKey,
  configInput: Partial<RecommendationConfig>,
  drawDate: string,
  jackpotCash: number,
  jackpotAnnuity: number | null = null,
  dataHealth: DataHealthSummary = buildDataHealth(data)
): GameRecommendation {
  const config = normalizeConfig(configInput);
  const game = games[gameKey];
  const sales = estimateSalesFromState(data, gameKey, drawDate, jackpotCash);
  const cost = ticketCost(game, config.use_powerplay);
  const playCount = Math.floor(config.budget / cost);
  const lowerEv = lowerTierEv(game, config.tax_keep_rate, config.use_powerplay);
  const avgShare = poissonSplitShareFactor(sales.sales / totalCombinations(game));
  const uniformPublicEv = lowerEv + jackpotProbability(game) * jackpotCash * config.tax_keep_rate * avgShare - cost;
  const roi = uniformPublicEv / cost;
  const chances = prizeChanceSummary(game, 100, config.tax_keep_rate, config.use_powerplay);
  const opScore = opportunityScore(uniformPublicEv, roi, chances, jackpotCash, cost);
  const candidatePool =
    playCount > 0
      ? buildCandidatePool(data, gameKey, jackpotCash, sales.sales, config, drawDate)
      : {
          tickets: [],
          stats: {
            legalTicketSpace: totalCombinations(game),
            ticketsTested: 0,
            searchAttempts: 0,
            medianScore: 0,
            medianAfterTaxValue: 0
          }
        };
  const candidates = candidatePool.tickets;
  const selectedBase = config.advisor_mode === "single_player" ? candidates.slice(0, Math.min(1, playCount)) : optimizePortfolio(candidates, playCount, config.max_overlap);
  const selected = selectedBase.map((ticket) => ({
    ...ticket,
    single_ticket_score_receipt: singleTicketScoreReceipt(
      data,
      gameKey,
      ticket,
      chances,
      opScore,
      config,
      jackpotCash,
      sales.sales,
      drawDate,
      candidatePool.stats,
      Math.max(1, candidates.findIndex((item) => item.ticket.compact === ticket.ticket.compact) + 1),
      dataHealthStatusForGame(dataHealth, gameKey)
    )
  }));
  const drawState = modelState(data, gameKey, "draw_bias");
  let drawWeight = config.draw_bias_mode === "off" ? 0 : Number(drawState.weight ?? 0);
  if (config.draw_bias_mode === "auto" && !drawState.active) drawWeight = 0;
  const salesState = modelState(data, gameKey, "sales_forecast");
  const humanState = modelState(data, gameKey, "human_pick");
  let externalStatus = "external_features_collected_but_inactive_until_sales_validation";
  if (salesState.active) externalStatus = "external_features_active_in_validated_sales_model";
  if (humanState.active) externalStatus += "; human_pick_tier_residual_model_active";
  const rec: GameRecommendation = {
    game_key: gameKey,
    game_name: game.name,
    draw_date: drawDate,
    ticket_cost: cost,
    budget: config.budget,
    play_count: playCount,
    jackpot_cash: jackpotCash,
    jackpot_annuity: jackpotAnnuity,
    estimated_sales: sales.sales,
    lower_tier_ev: lowerEv,
    ev_per_ticket_uniform_public: uniformPublicEv,
    roi_per_dollar: roi,
    draw_bias_weight: drawWeight,
    sales_model_status: sales.status,
    external_features_status: externalStatus,
    mathematical_profit_recommendation: uniformPublicEv > 0 ? "PLAY" : "DO_NOT_PLAY_FOR_PROFIT",
    entertainment_recommendation: uniformPublicEv <= 0 ? "Best only under strict entertainment budget." : "Positive modeled EV; still high variance.",
    tickets: selected,
    break_even_cash_no_split: noSplitBreakEvenCash(gameKey, config.tax_keep_rate, config.use_powerplay),
    break_even_cash_split_adjusted: 0,
    profit_maximizing_ticket_count: uniformPublicEv > 0 ? playCount : 0,
    portfolio_jackpot_probability: 0,
    portfolio_any_prize_probability_approx: 0,
    jackpot_probability: chances.jackpot_probability,
    any_prize_probability: chances.any_prize_probability,
    meaningful_prize_probability: chances.meaningful_prize_probability,
    meaningful_prize_floor: chances.meaningful_prize_floor,
    opportunity_score: opScore,
    opportunity_rank_reason: "",
    validation_summary: {}
  };
  rec.opportunity_rank_reason = opportunityReason(rec);
  const diag = attachGameDiagnostics(data, rec, config.tax_keep_rate, config.use_powerplay);
  rec.validation_summary = diag;
  rec.break_even_cash_split_adjusted = Number(diag.break_even_cash_jackpot_uniform_public ?? 0);
  rec.portfolio_jackpot_probability = Number(diag.portfolio_jackpot_probability_uniform ?? 0);
  rec.portfolio_any_prize_probability_approx = Number(diag.portfolio_any_prize_probability_approx ?? 0);
  return rec;
}

export function recommendForGame(data: RuntimeData, gameKey: GameKey, config: Partial<RecommendationConfig>, now = new Date()): GameRecommendation {
  const [drawDate, annuity, cash] = jackpotForRecommendation(data, gameKey, now);
  return recommendForGameAt(data, gameKey, config, drawDate, cash, annuity, buildDataHealth(data, now));
}

export function recommendAll(data: RuntimeData, configInput: Partial<RecommendationConfig> = {}, now = new Date()): FullRecommendation {
  const config = { ...normalizeConfig(configInput), advisor_mode: "portfolio" as const };
  const recs = gameKeys.map((key) => recommendForGame(data, key, config, now)).sort((a, b) => b.opportunity_score - a.opportunity_score);
  const selected = recs[0];
  const edgeGames: Record<string, unknown> = {};
  for (const rec of recs) {
    edgeGames[rec.game_key] = {
      name: rec.game_name,
      ev_per_ticket: rec.ev_per_ticket_uniform_public,
      roi_per_dollar: rec.roi_per_dollar,
      any_prize_probability: rec.any_prize_probability,
      meaningful_prize_probability: rec.meaningful_prize_probability,
      opportunity_score: rec.opportunity_score,
      profit_policy_ticket_count: rec.profit_maximizing_ticket_count,
      models: (rec.validation_summary.model_states ?? {}) as Record<string, unknown>
    };
  }
  return {
    generated_at: new Date().toISOString(),
    config,
    game_rankings: recs,
    selected_game_key: selected.game_key,
    selected_game_name: selected.game_name,
    edge_report: {
      summary: "Aggressive data collection; conservative evidence gating. External factors only affect recommendations through validated EV/sales/split-risk paths.",
      games: edgeGames
    },
    notes: [
      "Number choice cannot increase the physical jackpot odds under a fair draw; this engine optimizes EV, split-risk, and portfolio coverage.",
      "External variables such as weather and holidays affect recommendations only after temporal holdout validation, usually through sales or split-risk rather than ball probability.",
      "Public-pick popularity calibration affects split-risk only when sales and tier-winner labels pass the evidence gate.",
      "If mathematical_profit_recommendation is DO_NOT_PLAY_FOR_PROFIT, playing is entertainment spending, not investing."
    ]
  };
}

export function recommendAdvisor(data: RuntimeData, configInput: Partial<RecommendationConfig> = {}, now = new Date()): AdvisorPayload {
  const config = { ...normalizeConfig(configInput), advisor_mode: "single_player" as const };
  const generatedAt = now.toISOString();
  const sourceFreshness = buildSourceFreshness(data, now);
  const sourceQuality = buildSourceQuality(data, now);
  const dataHealth = buildDataHealth(data, now);
  const modelVersion = buildModelVersion(data, sourceFreshness, generatedAt);
  const gamesOut = gameKeys.map((gameKey) => {
    const game = games[gameKey];
    const cost = ticketCost(game, config.use_powerplay);
    const [drawDate, annuity, cash] = jackpotForRecommendation(data, gameKey, now);
    const rec = recommendForGameAt(data, gameKey, {
      ...config,
      budget: cost,
      advisor_mode: "single_player",
      random_seed_extra: `one-ticket-${config.random_seed_extra}`
    }, drawDate, cash, annuity, dataHealth);
    const ticket = rec.tickets[0];
    if (!ticket) {
      throw new Error(`No ticket generated for ${game.name}.`);
    }
    const trackedKey = `${rec.game_key}:${rec.draw_date}:${ticket.ticket.compact}`;
    return {
      recommendation_id: "",
      model_version: modelVersion,
      game_key: rec.game_key,
      game_name: rec.game_name,
      draw_date: rec.draw_date,
      draw_time_et: game.drawTimeEt,
      jackpot_cash: rec.jackpot_cash,
      jackpot_annuity: rec.jackpot_annuity,
      ticket_cost: rec.ticket_cost,
      profit_stance: rec.mathematical_profit_recommendation,
      ticket,
      recommendation: {
        ...rec,
        budget: cost,
        play_count: 1,
        tickets: [ticket],
        portfolio_jackpot_probability: rec.portfolio_jackpot_probability,
        portfolio_any_prize_probability_approx: rec.portfolio_any_prize_probability_approx
      },
      explanation: buildTicketExplanation(rec, ticket),
      data_status: buildGameDataStatus(data, rec),
      already_tracked: Boolean(data.registry?.includes(trackedKey))
    };
  });
  const opportunityRankings: GameOpportunity[] = [...gamesOut]
    .sort((a, b) => b.recommendation.opportunity_score - a.recommendation.opportunity_score)
    .map((advice, idx) => ({
      rank: idx + 1,
      game_key: advice.game_key,
      game_name: advice.game_name,
      draw_date: advice.draw_date,
      prize_amount: advice.jackpot_cash,
      ticket_cost: advice.ticket_cost,
      value_check: advice.recommendation.ev_per_ticket_uniform_public,
      roi_per_dollar: advice.recommendation.roi_per_dollar,
      jackpot_probability: advice.recommendation.jackpot_probability,
      any_prize_probability: advice.recommendation.any_prize_probability,
      meaningful_prize_probability: advice.recommendation.meaningful_prize_probability,
      data_confidence: advice.data_status.confidence_label,
      recommendation_label: advice.profit_stance === "PLAY" ? "Value play" : "Entertainment only",
      reason: advice.recommendation.opportunity_rank_reason
    }));
  const best = opportunityRankings[0];
  const positiveCount = gamesOut.filter((game) => game.profit_stance === "PLAY").length;
  const recommendationId = `rec_${stableSeed(
    generatedAt,
    config.user_id,
    gamesOut.map((game) => `${game.game_key}:${game.draw_date}:${game.ticket.ticket.compact}`).join("|")
  )
    .toString(16)
    .padStart(8, "0")}`;
  const gamesWithReceipt = gamesOut.map((game) => ({ ...game, recommendation_id: recommendationId }));
  const aiAudit = buildAiAudit(data, gamesWithReceipt, dataHealth, sourceFreshness, sourceQuality);
  return {
    recommendation_id: recommendationId,
    model_version: modelVersion,
    generated_at: generatedAt,
    config,
    total_cost: gamesWithReceipt.reduce((sum, game) => sum + game.ticket_cost, 0),
    one_line_takeaway:
      positiveCount > 0
        ? `${positiveCount} value play${positiveCount === 1 ? "" : "s"} today.`
        : "Optional plays today.",
    best_game_key: best.game_key,
    best_game_name: best.game_name,
    best_game_reason: `${best.game_name}: ${best.reason}`,
    data_health: dataHealth,
    source_freshness: sourceFreshness,
    source_quality: sourceQuality,
    learning_scope: buildLearningScope(data, now),
    outside_signal_audit: buildOutsideSignalAudit(data, now),
    ai_audit: aiAudit,
    play_projection: buildPlayProjection(gamesWithReceipt, generatedAt),
    personal_calibration_summary: emptyPersonalCalibrationSummary(data),
    opportunity_rankings: opportunityRankings,
    games: gamesWithReceipt
  };
}

function weightedAverage(values: Array<{ value: number; weight: number }>, fallback = 0): number {
  const totalWeight = values.reduce((sum, row) => sum + Math.max(0, row.weight), 0);
  if (totalWeight <= 0) return fallback;
  return values.reduce((sum, row) => sum + row.value * Math.max(0, row.weight), 0) / totalWeight;
}

function deepSearchReceipt(
  selected: NonNullable<TicketScore["single_ticket_score_receipt"]>,
  receipts: Array<NonNullable<TicketScore["single_ticket_score_receipt"]>>,
  passCount: number
): NonNullable<TicketScore["single_ticket_score_receipt"]> {
  const fairDrawNumberPolicy = selected.selection_notes.some((note) => note.includes("unbiased legal ticket"));
  if (fairDrawNumberPolicy) {
    return {
      ...selected,
      candidate_search_count: 1,
      selection_notes: [
        ...selected.selection_notes.filter((note) => !note.startsWith("Deep background search")),
        "Deep search held because no validated jackpot-number signal is active."
      ],
      simulation_field_comparison: {
        ...selected.simulation_field_comparison,
        tickets_tested: 1,
        search_attempts: 1,
        coverage_percent: Math.max(0, Math.min(1, 1 / Math.max(selected.simulation_field_comparison.legal_ticket_space, 1))),
        selected_rank: 1,
        selected_percentile: 1,
        median_score: selected.simulation_field_comparison.selected_score,
        selected_score_lift: 0,
        median_after_tax_value: selected.simulation_field_comparison.selected_after_tax_value,
        selected_value_lift: 0,
        confidence_label: "Fair-draw receipt",
        plain_english: "No validated jackpot-number signal is active, so no deeper number search is credited for this draw."
      }
    };
  }
  const tested = receipts.reduce((sum, receipt) => sum + receipt.simulation_field_comparison.tickets_tested, 0);
  const attempts = receipts.reduce((sum, receipt) => sum + receipt.simulation_field_comparison.search_attempts, 0);
  const legalSpace = selected.simulation_field_comparison.legal_ticket_space;
  const medianScore = weightedAverage(
    receipts.map((receipt) => ({
      value: receipt.simulation_field_comparison.median_score,
      weight: receipt.simulation_field_comparison.tickets_tested
    })),
    selected.simulation_field_comparison.median_score
  );
  const medianValue = weightedAverage(
    receipts.map((receipt) => ({
      value: receipt.simulation_field_comparison.median_after_tax_value,
      weight: receipt.simulation_field_comparison.tickets_tested
    })),
    selected.simulation_field_comparison.median_after_tax_value
  );
  const confidenceLabel =
    tested >= 50_000
      ? "High process confidence"
      : tested >= 4_900
        ? "Good process confidence"
        : "Limited process confidence";
  return {
    ...selected,
    candidate_search_count: tested,
    selection_notes: [...selected.selection_notes.filter((note) => !note.startsWith("Deep background search")), `Deep background search used ${passCount} independent passes.`],
    simulation_field_comparison: {
      ...selected.simulation_field_comparison,
      tickets_tested: tested,
      search_attempts: attempts,
      coverage_percent: Math.max(0, Math.min(1, tested / Math.max(legalSpace, 1))),
      selected_rank: 1,
      selected_percentile: 1,
      median_score: medianScore,
      selected_score_lift: selected.simulation_field_comparison.selected_score - medianScore,
      median_after_tax_value: medianValue,
      selected_value_lift: selected.simulation_field_comparison.selected_after_tax_value - medianValue,
      confidence_label: confidenceLabel,
      plain_english: `${tested.toLocaleString("en-US")} legal tickets were tested across ${passCount} independent background passes. This ticket ranked #1 in the combined field.`
    }
  };
}

export function recommendAdvisorDeep(data: RuntimeData, configInput: Partial<RecommendationConfig> = {}, now = new Date(), passes = 4): AdvisorPayload {
  const config = normalizeConfig(configInput);
  const passCount = Math.max(1, Math.min(12, Math.trunc(passes)));
  const payloads = Array.from({ length: passCount }, (_, idx) =>
    recommendAdvisor(
      data,
      {
        ...config,
        random_seed_extra: idx === 0 ? config.random_seed_extra : [config.random_seed_extra, `deep-pass-${idx}`].filter(Boolean).join(":")
      },
      now
    )
  );
  const base = payloads[0];
  const gamesWithDeepSearch = base.games.map((game) => {
    const advices = payloads.flatMap((payload) => payload.games.filter((candidate) => candidate.game_key === game.game_key));
    const best = advices.reduce((current, candidate) => (candidate.ticket.score > current.ticket.score ? candidate : current), advices[0]);
    const receipts = advices.flatMap((advice) => advice.ticket.single_ticket_score_receipt ? [advice.ticket.single_ticket_score_receipt] : []);
    const selectedReceipt = best.ticket.single_ticket_score_receipt && receipts.length ? deepSearchReceipt(best.ticket.single_ticket_score_receipt, receipts, passCount) : best.ticket.single_ticket_score_receipt;
    const ticket = { ...best.ticket, single_ticket_score_receipt: selectedReceipt };
    const recommendation = { ...best.recommendation, tickets: [ticket] };
    return {
      ...best,
      ticket,
      recommendation,
      explanation: buildTicketExplanation(recommendation, ticket)
    };
  });
  const generatedAt = base.generated_at;
  const recommendationId = `rec_${stableSeed(
    generatedAt,
    config.user_id,
    gamesWithDeepSearch.map((game) => `${game.game_key}:${game.draw_date}:${game.ticket.ticket.compact}`).join("|"),
    `deep:${passCount}`
  )
    .toString(16)
    .padStart(8, "0")}`;
  const gamesWithId = gamesWithDeepSearch.map((game) => ({ ...game, recommendation_id: recommendationId }));
  return {
    ...base,
    recommendation_id: recommendationId,
    config,
    learning_scope: buildLearningScope(data, now),
    outside_signal_audit: buildOutsideSignalAudit(data, now),
    ai_audit: buildAiAudit(data, gamesWithId, base.data_health, base.source_freshness, base.source_quality),
    play_projection: buildPlayProjection(gamesWithId, generatedAt),
    games: gamesWithId
  };
}

export function combineAdvisorPayloads(data: RuntimeData, payloadsInput: AdvisorPayload[], configInput: Partial<RecommendationConfig> = {}, now = new Date()): AdvisorPayload {
  const payloads = payloadsInput.filter((payload) => payload?.games?.length);
  if (!payloads.length) return recommendAdvisor(data, configInput, now);
  if (payloads.length === 1) return payloads[0];

  const config = normalizeConfig({ ...payloads[0].config, ...configInput, random_seed_extra: "" });
  const base = payloads[0];
  const gamesWithCombinedSearch = base.games.map((game) => {
    const advices = payloads.flatMap((payload) => payload.games.filter((candidate) => candidate.game_key === game.game_key));
    const best = advices.reduce((current, candidate) => (candidate.ticket.score > current.ticket.score ? candidate : current), advices[0]);
    const receipts = advices.flatMap((advice) => (advice.ticket.single_ticket_score_receipt ? [advice.ticket.single_ticket_score_receipt] : []));
    const selectedReceipt =
      best.ticket.single_ticket_score_receipt && receipts.length
        ? deepSearchReceipt(best.ticket.single_ticket_score_receipt, receipts, payloads.length)
        : best.ticket.single_ticket_score_receipt;
    const ticket = { ...best.ticket, single_ticket_score_receipt: selectedReceipt };
    const recommendation = { ...best.recommendation, tickets: [ticket] };
    return {
      ...best,
      ticket,
      recommendation,
      explanation: buildTicketExplanation(recommendation, ticket)
    };
  });
  const generatedAt = now.toISOString();
  const recommendationId = `rec_${stableSeed(
    generatedAt,
    config.user_id,
    gamesWithCombinedSearch.map((game) => `${game.game_key}:${game.draw_date}:${game.ticket.ticket.compact}`).join("|"),
    `combined:${payloads.map((payload) => payload.recommendation_id).join("|")}`
  )
    .toString(16)
    .padStart(8, "0")}`;
  const gamesWithId = gamesWithCombinedSearch.map((game) => ({ ...game, recommendation_id: recommendationId }));
  return {
    ...base,
    recommendation_id: recommendationId,
    generated_at: generatedAt,
    config,
    learning_scope: buildLearningScope(data, now),
    outside_signal_audit: buildOutsideSignalAudit(data, now),
    ai_audit: buildAiAudit(data, gamesWithId, base.data_health, base.source_freshness, base.source_quality),
    play_projection: buildPlayProjection(gamesWithId, generatedAt),
    games: gamesWithId
  };
}

export function seedJackpotsForToday(now = new Date()): JackpotSnapshot[] {
  return gameKeys.map((gameKey) => {
    const game: GameConfig = games[gameKey];
    return {
      game_key: gameKey,
      draw_date: nextDrawDate(now, game.drawDays),
      jackpot_annuity: null,
      jackpot_cash: game.jackpotCashDefault,
      source: gameKey === "millionaire_for_life" ? "static_official_prize_chart" : "default_no_live_snapshot",
      observed_at: new Date().toISOString(),
      raw_hash: "client_default" as never
    };
  });
}

export function buildClientRuntime(now = new Date()): RuntimeData {
  const snapshots = seedJackpotsForToday(now);
  return {
    ...emptyRuntimeData(),
    jackpots: Object.fromEntries(snapshots.map((snapshot) => [snapshot.game_key, snapshot])) as RuntimeData["jackpots"],
    jackpotSnapshots: snapshots
  };
}

export function registryKeysFromRecommendation(rec: GameRecommendation, userId: string): string[] {
  return rec.tickets.map((ticket) => `${rec.game_key}:${rec.draw_date}:${ticket.ticket.compact}:${userId}`);
}

export function ticketFromCompact(compact: string): Ticket | null {
  const match = compact.match(/^(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})\+(\d{2})$/);
  if (!match) return null;
  return createTicket(match.slice(1, 6).map(Number), Number(match[6]));
}

export { games, gameKeys, isoDate };
